import React, { useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import api_requests from "../../resources/api_requests";
import toast from "react-hot-toast";
import Card from "../../modules/Card";
import Spinner from "../../modules/Spinner";
import Input from "../../modules/Input";
import AppNavBarWithBackAction from "../../modules/AppNavBarWithBackAction";

const category_data = [
  {
    label: "Pre-pickup",
    value: "PRE_PICKUP",
    subcategory: [
      { label: "No Rider Allocated", value: "NO_RIDER_ALLOCATED" },
      { label: "Rider Hasn't Reached", value: "RIDER_NOT_REACHED_PICKUP" },
      { label: "Rider denied pickup", value: "RIDER_DENIED_PICKUP" },
    ],
  },
  {
    label: "Post-pickup",
    value: "POST_PICKUP",
    subcategory: [
      { label: "Rider Non-responsive", value: "RIDER_NON_RESPONSIVE" },
      { label: "Rider Behaviour Issue", value: "RIDER_BEHAVIOR_ISSUE" },
      {
        label: "Delayed or Breached Delivery",
        value: "DELAYED_BREACHED_DELIVERY",
      },
      { label: "RTO Not Done", value: "RTO_NOT_DONE" },
      {
        label: "Final Status Mismatch  or missing",
        value: "FINAL_STATUS_MISMATCH_MISSING",
      },
    ],
  },
  {
    label: "Dispute",
    value: "DISPUTE",
    subcategory: [
      { label: "COD Dispute", value: "COD_DISPUTE" },
      { label: "Delivery or RTO Damaged", value: "ORDER_DAMAGED" },
      { label: "Items missing in order", value: "SOME_ITEMS_MISSING" },
      { label: "Marked Pickup Not Picked", value: "MARKED_PICKUP_NO_PICKUP" },
      {
        label: "Order or RTO Marked Delivered Not Delivered",
        value: "MARKED_COMPLETE_NOT_COMPLETE",
      },
    ],
  },
  {
    label: "other",
    value: "OTHER",
    subcategory: [{ label: "other", value: "OTHER" }],
  },
];

function CreateSupportTicket() {
  const navigate = useNavigate();
  const location = useLocation();
  const [errors, setErrors] = useState({});
  const [isFormValid, setFormValidation] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const { cdr_id } = location.state || {};

  // We need the set for the sub-category population
  const [category, setCategory] = useState(undefined);

  const categoryRef = useRef();
  const subCategoryRef = useRef();
  const emailRef = useRef();
  const contactRef = useRef();
  const queryRef = useRef();

  const formData = useMemo(() => new Map(), []);

  const form = useMemo(
    () => [
      {
        name: "category",
        label: "Category*",
        placeholder: "Select support category",
        ref: categoryRef,
        type: "select",
        selectOptions: {
          options: category_data,
          groupLabel: "Category Selection",
        },
        value: undefined,
      },
      {
        name: "subCategory",
        label: "Sub-Category*",
        placeholder: "Select support sub-category",
        ref: subCategoryRef,
        type: "select",
        selectOptions: {
          options:
            category_data.find((cat) => cat.value === category)?.subcategory ||
            [],
          groupLabel: "Sub-Category Selection",
        },
        value: undefined,
      },
      {
        name: "email",
        label: "Email*",
        placeholder: "Eg: yourname@gmail.com",
        ref: emailRef,
        type: "email",
        value: undefined,
      },
      {
        name: "contact",
        label: "Mobile Number*",
        placeholder: "Enter 10 digit mobile number",
        ref: contactRef,
        type: "tel",
        value: undefined,
      },
      {
        name: "query",
        label: "Query*",
        placeholder: "Enter query to resolve",
        ref: queryRef,
        type: "text",
        value: undefined,
        textOptions: {
          characterLimit: 100,
        },
      },
    ],
    [category]
  );

  const validators = {
    category: (value) => {
      if (!(value && value.trim())) return "Category is required";
      return "";
    },
    subCategory: (value) => {
      if (!(value && value.trim())) return "Sub-Category is required";
      return "";
    },
    email: (value) => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!(value && value.trim())) return "Email is required";
      if (!emailPattern.test(value)) return "Please enter a valid email";
      return "";
    },
    contact: (value) => {
      if (!(value && value.trim())) return "Mobile number is required";
      if (!/^\d+$/.test(value)) return "Contact should contain only digits";
      if (value.length !== 10) return "Contact must be exactly 10 digits";
      return "";
    },
    query: (value) => {
      if (!(value && value.trim())) return "Query is required";
      return "";
    },
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    form.forEach((field) => {
      const value = formData.get(field.name);
      const fieldError = validators[field.name](value);
      if (fieldError) {
        newErrors[field.name] = fieldError;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload = {
        pidge_id: cdr_id,
        issue_category: formData.get("category"), // set enum
        issue_subcategory: formData.get("subCategory"), // set enum
        description: formData.get("query"), // free text
        update_info: {
          poc_number: formData.get("contact"), // optional
          poc_email: formData.get("email"), // optional
        },
      };
      await api_requests.create_ticket(payload);
      toast.success("Ticket Created");
      navigate("/ticket-history");
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  function handleChange(name, value) {
    if (
      value instanceof Event ||
      (typeof value === "object" &&
        "target" in value &&
        value.target instanceof Element)
    ) {
      value = value.target.value;
    }

    formData.set(name, value);
    let isValid = true;

    // trigger an update so that the
    // sub-category menu is populated
    if (name === "category") {
      setCategory(value);
    }

    form.forEach((field) => {
      const fieldError = validators[name](formData.get(field.name));
      if (fieldError) {
        isValid = false;
      }
    });

    if (isValid) {
      setFormValidation(true);
    } else {
      setFormValidation(false);
    }
  }

  return (
    <div className="px-6 mt-10">
      <AppNavBarWithBackAction title="Ticket History" />
      <div className="mt-8">
        <div className="px-6 mt-10">
          <div className="flex flex-col items-center gap-0.5 justify-center">
            <p className="font-custom  text-2xl font-semibold text-[#7953D2] ">
              Support
            </p>
            <p className="font-custom text-xs font-medium text-dark_gray ">
              Share your queries with us, and we'll get back to you as soon as
              possible!
            </p>
          </div>

          <Card>
            <div className="flex flex-col gap-[20px]">
              {form.map(
                ({
                  name,
                  label,
                  placeholder,
                  type,
                  ref,
                  selectOptions,
                  textOptions,
                }) => {
                  return (
                    <div key={name}>
                      <Input
                        label={label}
                        placeholder={placeholder}
                        ref={ref}
                        onChange={(value) => handleChange(name, value)}
                        type={type}
                        selectOptions={selectOptions}
                        textOptions={textOptions}
                        disabled={
                          isLoading ||
                          // disable the select if there are no options
                          (type === "select" &&
                            Array.isArray(selectOptions.options) &&
                            selectOptions.options.length === 0)
                        }
                      />
                      {errors[name] && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors[name]}
                        </p>
                      )}
                    </div>
                  );
                }
              )}
            </div>

            <div className="mt-[34px]">
              <button
                onClick={handleSubmit}
                className="font-custom btn btn-block bg-[#492897] text-white rounded-[30px] text-base font-semibold disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Spinner heightResponsive />
                ) : (
                  <span>Continue</span>
                )}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default CreateSupportTicket;
